import React, { useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { BookingJournalDA } from '../../services/admin/booking.journal.service';
import { Error } from '../../models/error.model';
import { Company } from '../../models/company.model';
import { Message } from '../../models/message.model';
import { GlobalSwal } from '../../styles/swal.style';
import { legendIconValidInvalidRestart, legendIconInvalid } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { evaluateLegendRequiredIcon } from '../../scripts/validate.legend.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { getError, getMessage } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppLegend from '../element/Legend';

export interface AppModalBookingJournalCreateProps {
  companyForUser: Company | null,
  loadBookingJournals: Function
};

let errorResponse: Error, messageResponse: Message;

const AppModalBookingJournalCreate: React.FunctionComponent<AppModalBookingJournalCreateProps> = ({companyForUser, loadBookingJournals}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [name, setName] = useState({value: '', valid: false})

  const submitBookingJournalCreate = async () => {
    setLoadIndicator('on')

    if (companyForUser && name.valid) {
      await BookingJournalDA.postBookingJournal(companyForUser.id, name.value).then( (response) => {
        if (response.status === 201) {
          messageResponse = response.data
          loadBookingJournals(companyForUser.id)

          GlobalSwal.fire({
            title: getMessage(messageResponse.message, lang.code),
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalBookingJournalCreate()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!name.value.length) legendIconInvalid('modal-booking-journal-create-input-name', 'modal-booking-journal-create-legend-name-required')
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setName({...name, value: event.target.value})
  }

  const validateName = () => {
    evaluateLegendRequiredIcon(expressions.text, name, setName, 'modal-booking-journal-create-input-name', 'modal-booking-journal-create-legend-name-valid', 'modal-booking-journal-create-legend-name-required')
  }

  const executeHideModalBookingJournalCreate = () => {
    modalHide('modal-booking-journal-create')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendIconValidInvalidRestart('modal-booking-journal-create-input-name', 'modal-booking-journal-create-legend-name-valid')
    legendIconValidInvalidRestart('modal-booking-journal-create-input-name', 'modal-booking-journal-create-legend-name-required')

    setName({value: '', valid: false})
  }

  return (
    <div id="modal-booking-journal-create" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.addBookingJournal}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <div className="mb-3 fw-bold">{lang.labels.newManualAccountingBookCreated}</div>
              <div className="row mb-2">
                <label className="col-lg-3 col-form-label">{lang.labels.type}</label>
                <label className="col-lg-9 d-flex align-items-center">{lang.labels.manuallyCreated}</label>
              </div>
              <div className="row">
                <label className="col-lg-3 col-form-label required">{lang.labels.name}</label>
                <div className="col-lg-9">
                  <input id="modal-booking-journal-create-input-name" className="form-control" type="text" autoComplete="off" name="name" value={name.value} onChange={handleChangeName} onKeyUp={validateName} onBlur={validateName} />
                  <AppLegend component={"modal-booking-journal-create"} attribute={{check: name.valid, name: "name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalBookingJournalCreate}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitBookingJournalCreate}>
              <span className="indicator-label">{lang.labels.create}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalBookingJournalCreate;
